import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { isChunkLoadError } from '@/lib/lazy-with-retry';

// User-friendly error handler for better UX
export function UserFriendlyErrorHandler() {
  const { toast } = useToast();
  const { t } = useTranslation('common');

  useEffect(() => {
    // Global error handler for uncaught errors
    const handleGlobalError = (event: ErrorEvent) => {
      console.error('Global error caught:', event.error);

      // Check for stale-chunk / dynamic-import errors first.
      // The lazyWithRetry utility handles the automatic reload, but if
      // the error still bubbles up (e.g. after a reload already happened),
      // show a user-friendly message with a reload button.
      if (isChunkLoadError(event.error)) {
        toast({
          title: t('errors.appUpdated'),
          description: t('errors.chunkLoadFailed'),
          variant: 'destructive',
        });
        event.preventDefault();
        return;
      }
      
      // Check for AI quota errors
      if (event.error?.message?.includes('quota') || event.error?.message?.includes('429')) {
        toast({
          title: "🤖 KI-Assistent temporär nicht verfügbar",
          description: "Das monatliche Kontingent für die KI-Bildanalyse ist aufgebraucht. Der Service wird am Monatsanfang automatisch wieder aktiviert.",
          variant: "destructive",
        });
        return;
      }
      
      // Check for network errors
      if (event.error?.message?.includes('fetch') || event.error?.message?.includes('network')) {
        toast({
          title: "📡 Verbindungsproblem",
          description: "Es gab ein Problem bei der Verbindung zum Server. Bitte versuchen Sie es erneut.",
          variant: "destructive",
        });
        return;
      }
      
      // General user-friendly message
      toast({
        title: "⚠️ Ein Fehler ist aufgetreten",
        description: "Es gab ein technisches Problem. Falls der Fehler weiterhin auftritt, wenden Sie sich bitte an den Support.",
        variant: "destructive",
      });
    };

    // Promise rejection handler
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);

      // Check for stale-chunk / dynamic-import errors
      if (isChunkLoadError(event.reason)) {
        toast({
          title: t('errors.appUpdated'),
          description: t('errors.chunkLoadFailed'),
          variant: 'destructive',
        });
        event.preventDefault();
        return;
      }
      
      // AI quota errors
      if (event.reason?.status === 429 || event.reason?.message?.includes('quota')) {
        toast({
          title: "🤖 KI-Assistent temporär nicht verfügbar", 
          description: "Das monatliche Kontingent für die KI-Bildanalyse ist aufgebraucht. Der Service wird am Monatsanfang automatisch wieder aktiviert.",
          variant: "destructive",
        });
        event.preventDefault();
        return;
      }
      
      // General user-friendly message
      toast({
        title: "⚠️ Ein Fehler ist aufgetreten",
        description: "Es gab ein technisches Problem. Falls der Fehler weiterhin auftritt, wenden Sie sich bitte an den Support.",
        variant: "destructive",
      });
      
      event.preventDefault();
    };

    // Add event listeners
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Cleanup
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [toast, t]);

  return null; // Invisible component
}
